import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const PROMPT_TYPES = ['ICP Generation', 'Sequence — Email', 'Sequence — LinkedIn', 'Intelligence', 'Research', 'Other'];
const SECTORS = [
  'Technology, Information and Media', 'Financial Services', 'Hospitals and Health Care',
  'Manufacturing', 'Professional Services', 'Consumer Services', 'Retail', 'Education',
  'Real Estate and Equipment Rental', 'Construction', 'Transportation, Logistics, Supply Chain and Storage',
  'Energy, Mining and Utilities', 'Government Administration', 'Non-profit Organizations',
  'Entertainment Providers', 'Accommodation and Food Services', 'Administrative and Support Services',
  'Farming, Ranching and Forestry', 'Civic and Social Organizations', 'Other'
];


const inputStyle = { width: '100%', backgroundColor: '#fff', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 0.8rem', color: '#1A1A1A', fontSize: '0.875rem', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
const labelStyle = { display: 'block', fontSize: '0.8rem', color: '#4A5568', marginBottom: '0.35rem', fontWeight: '500' };

function PromptLibrary() {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [formData, setFormData] = useState({ title: '', prompt_text: '', prompt_type: '', sector_tag: '', performance_notes: '' });
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchPrompts(); }, []);

  function handleTagKeyDown(e) {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const val = tagInput.trim().replace(/,$/, '');
      if (val && !tags.includes(val)) {
        const newTags = [...tags, val];
        setTags(newTags);
        setFormData(prev => ({ ...prev, performance_notes: newTags.join(', ') }));
      }
      setTagInput('');
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      const newTags = tags.slice(0, -1);
      setTags(newTags);
      setFormData(prev => ({ ...prev, performance_notes: newTags.join(', ') }));
    }
  }

  function removeTag(tag) {
    const newTags = tags.filter(t => t !== tag);
    setTags(newTags);
    setFormData(prev => ({ ...prev, performance_notes: newTags.join(', ') }));
  }

  async function fetchPrompts() {
    try {
      const res = await fetch(`${API_BASE}/prompts`);
      setPrompts(await res.json());
    } catch (err) { setError('Could not load prompts.'); }
  }

  async function handleSave() {
    if (!formData.title?.trim() || !formData.prompt_text?.trim()) return;
    setSaving(true);
    try {
      const url = editingPrompt ? `${API_BASE}/prompts/${editingPrompt.id}` : `${API_BASE}/prompts`;
      const method = editingPrompt ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
      if (!res.ok) throw new Error(await res.text());
      await fetchPrompts();
      setShowForm(false);
      setEditingPrompt(null);
      setFormData({ title: '', prompt_text: '', prompt_type: '', sector_tag: '', performance_notes: '' });
      setTags([]);
    } catch (err) { setError('Could not save prompt.'); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this prompt?')) return;
    try {
      await fetch(`${API_BASE}/prompts/${id}`, { method: 'DELETE' });
      setPrompts(prompts.filter(p => p.id !== id));
    } catch (err) { setError('Could not delete prompt.'); }
  }

  function handleEdit(prompt) {
    setEditingPrompt(prompt);
    const existingTags = prompt.performance_notes ? prompt.performance_notes.split(',').map(t => t.trim()).filter(Boolean) : [];
    setTags(existingTags);
    setFormData({ title: prompt.title || '', prompt_text: prompt.prompt_text || '', prompt_type: prompt.prompt_type || '', sector_tag: prompt.sector_tag || '', performance_notes: prompt.performance_notes || '' });
    setShowForm(true);
  }

  async function handleCopy(prompt) {
    navigator.clipboard.writeText(prompt.prompt_text);
    await fetch(`${API_BASE}/prompts/${prompt.id}/use`, { method: 'POST' });
    setPrompts(prompts.map(p => p.id === prompt.id ? { ...p, use_count: (p.use_count || 0) + 1 } : p));
  }

  const filtered = prompts.filter(p => {
    const matchSearch = !search.trim() || p.title?.toLowerCase().includes(search.toLowerCase()) || p.prompt_text?.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'all' || p.prompt_type === typeFilter;
    const matchSector = sectorFilter === 'all' || p.sector_tag === sectorFilter;
    return matchSearch && matchType && matchSector;
  });

  const types = [...new Set(prompts.map(p => p.prompt_type).filter(Boolean))];
  const sectors = [...new Set(prompts.map(p => p.sector_tag).filter(Boolean))];

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      <div style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto', boxSizing: 'border-box' }}>

        <button onClick={() => navigate('/clients')} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>
          ← All clients
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>Prompt Library</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>{prompts.length} prompts saved</p>
          </div>
          <button onClick={() => { setShowForm(true); setEditingPrompt(null); setFormData({ title: '', prompt_text: '', prompt_type: '', sector_tag: '', performance_notes: '' }); setTags([]); }}
            style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500' }}>
            + New Prompt
          </button>
        </div>

        {error && <p style={{ color: '#C53030', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</p>}

        {showForm && (
          <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: '500', marginTop: 0, marginBottom: '1.5rem', color: '#1A1A1A' }}>{editingPrompt ? 'Edit prompt' : 'New prompt'}</h2>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Title *</label>
              <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Accounting sector ICP prompt" style={inputStyle} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Type</label>
                <select value={formData.prompt_type} onChange={e => setFormData({ ...formData, prompt_type: e.target.value })} style={inputStyle}>
                  <option value="">Select type</option>
                  {PROMPT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Sector</label>
                <select value={formData.sector_tag} onChange={e => setFormData({ ...formData, sector_tag: e.target.value })} style={inputStyle}>
                  <option value="">Select sector</option>
                  {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Prompt text *</label>
              <textarea value={formData.prompt_text} onChange={e => setFormData({ ...formData, prompt_text: e.target.value })} placeholder="Write your prompt here..." rows={6} style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.6' }} />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Tags</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', padding: '0.5rem 0.6rem', backgroundColor: '#FAFAF8', border: '1px solid #D4CFC9', borderRadius: '6px', minHeight: '42px', alignItems: 'center' }}>
                {tags.map(tag => (
                  <span key={tag} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', backgroundColor: '#F5E6E6', color: '#8B1A1A', fontSize: '0.78rem', padding: '0.2rem 0.5rem', borderRadius: '20px', fontWeight: '500' }}>
                    {tag}
                    <button onClick={() => removeTag(tag)} style={{ background: 'none', border: 'none', color: '#8B1A1A', cursor: 'pointer', fontSize: '0.75rem', padding: 0, lineHeight: 1 }}>×</button>
                  </span>
                ))}
                <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={handleTagKeyDown}
                  placeholder={tags.length === 0 ? "Add tags — press comma or Enter to save..." : ""}
                  style={{ flex: 1, minWidth: '120px', background: 'none', border: 'none', color: '#1A1A1A', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit' }} />
              </div>
              <p style={{ margin: '0.3rem 0 0', fontSize: '0.75rem', color: '#888' }}>e.g. cold outreach, UK market, 10-50 staff</p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleSave} disabled={saving || !formData.title?.trim() || !formData.prompt_text?.trim()}
                style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '500', opacity: saving || !formData.title?.trim() || !formData.prompt_text?.trim() ? 0.5 : 1 }}>
                {saving ? 'Saving...' : editingPrompt ? 'Update prompt' : 'Save prompt'}
              </button>
              <button onClick={() => { setShowForm(false); setEditingPrompt(null); setTags([]); setTagInput(''); }}
                style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.6rem 1.2rem', fontSize: '0.875rem', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search prompts..."
            style={{ ...inputStyle, maxWidth: '260px' }} />
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ backgroundColor: '#fff', border: '1px solid #D4CFC9', borderRadius: '6px', color: '#1A1A1A', padding: '0.6rem 0.8rem', fontSize: '0.85rem', outline: 'none' }}>
            <option value="all">All types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={sectorFilter} onChange={e => setSectorFilter(e.target.value)}
            style={{ backgroundColor: '#fff', border: '1px solid #D4CFC9', borderRadius: '6px', color: '#1A1A1A', padding: '0.6rem 0.8rem', fontSize: '0.85rem', outline: 'none' }}>
            <option value="all">All sectors</option>
            {sectors.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ fontSize: '0.8rem', color: '#888' }}>{filtered.length} prompts</span>
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#888', backgroundColor: '#fff', borderRadius: '10px', border: '0.5px solid #E8E4E0' }}>
            <p style={{ margin: 0, fontWeight: '500', color: '#4A5568' }}>{prompts.length === 0 ? 'No prompts yet.' : 'No prompts match your filters.'}</p>
            {prompts.length === 0 && <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>Click "New Prompt" to add your first.</p>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filtered.map(prompt => (
              <div key={prompt.id} style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '1rem 1.25rem', cursor: 'pointer' }}
                  onClick={() => setExpandedId(expandedId === prompt.id ? null : prompt.id)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                      <p style={{ margin: 0, fontWeight: '500', fontSize: '0.9rem', color: '#1A1A1A' }}>{prompt.title || 'Untitled'}</p>
                      {prompt.prompt_type && <span style={{ fontSize: '0.68rem', color: '#8B1A1A', backgroundColor: '#F5E6E6', padding: '0.15rem 0.4rem', borderRadius: '20px', fontWeight: '500' }}>{prompt.prompt_type}</span>}
                      {prompt.sector_tag && <span style={{ fontSize: '0.68rem', color: '#4A5568', backgroundColor: '#E2E8F0', padding: '0.15rem 0.4rem', borderRadius: '20px', fontWeight: '500' }}>{prompt.sector_tag}</span>}
                      {prompt.use_count > 0 && <span style={{ fontSize: '0.72rem', color: '#888' }}>Used {prompt.use_count}x</span>}
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: expandedId === prompt.id ? 'pre-wrap' : 'nowrap' }}>
                      {prompt.prompt_text}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem', flexShrink: 0 }}>
                    <button onClick={e => { e.stopPropagation(); handleCopy(prompt); }} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', color: '#1A56A0', fontSize: '0.75rem', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Copy</button>
                    <button onClick={e => { e.stopPropagation(); handleEdit(prompt); }} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', color: '#4A5568', fontSize: '0.75rem', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Edit</button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(prompt.id); }} style={{ background: 'none', border: '1px solid #FEB2B2', borderRadius: '20px', color: '#C53030', fontSize: '0.75rem', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>Delete</button>
                  </div>
                </div>
                {prompt.performance_notes && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', padding: '0 1.25rem 0.75rem' }}>
                    {prompt.performance_notes.split(',').map(t => t.trim()).filter(Boolean).map(tag => (
                      <span key={tag} style={{ fontSize: '0.72rem', color: '#8B1A1A', backgroundColor: '#F5E6E6', padding: '0.15rem 0.45rem', borderRadius: '20px', fontWeight: '500' }}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PromptLibrary;
